/**
 * 店舗がエリアに属するかの判定
 *
 * エリアの定義が持つ条件と、店舗から取り出した判定値の突き合わせを持つ
 */

/**
 * 判定の方法
 *
 * 値はバックエンドから受け取る定義に入っているものと揃える
 */
export const AREA_MATCH_KIND = {
  /**
   * 前方一致
   *
   * 都道府県は住所の先頭に来るので、先頭だけを見れば足りる
   * 「東京都府中市」が「京都府」に一致するような、部分一致で起きる誤りも防げる
   */
  PREFIX: 'prefix',

  /**
   * 部分一致
   *
   * 国名は住所のどこに現れるか決まっていないので、部分一致で判定する
   * 例）「台湾台北市信義区…」と「No.7, Sec. 5, Xinyi Rd, Taiwan」のどちらも一致とする
   */
  INCLUDES: 'includes',
} as const satisfies Record<string, AreaLabelAreaMatch['kind']>

/**
 * 判定に使える予約フィールドのキー
 *
 * Point直下のうち、店舗ごとの属性を文字列で持つものだけを並べる
 * 緯度経度のような数値、distanceFromのような表示のための値は判定に使えない
 *
 * 追加フィールドはフィールド設定の is_extra で判別できるが、
 * 予約フィールドはPointの型から実行時に引けないので、ここに並べて突き合わせる
 */
export const TARGETABLE_RESERVED_FIELDS = ['key', 'name', 'address'] as const

/**
 * TARGETABLE_RESERVED_FIELDS が取る値
 */
export type TargetableReservedField = typeof TARGETABLE_RESERVED_FIELDS[number]

/**
 * 店舗がそのエリアに属するか
 *
 * 判定値を持たない定義と、判定に使うフィールドの値が空の店舗は、いずれも一致しない扱いにする
 *
 * @param point 判定する店舗
 * @param area 判定先のエリア、条件はバックエンドから受け取った定義が持つ
 * @param targetField 判定に使うフィールドのキー
 *   組織の設定と既定値のどちらを使うかはバックエンドが解決する
 */
export function matchesArea(point: Point, area: AreaLabelArea, targetField: string): boolean {
  const target = _resolveTargetValue(point, targetField)

  if (!target) {
    return false
  }

  const match = area.match

  if (match.excludes?.some(value => target.includes(value))) {
    return false
  }

  switch (match.kind) {
    case AREA_MATCH_KIND.PREFIX:
      return match.values.some(value => target.startsWith(value))
    case AREA_MATCH_KIND.INCLUDES:
      return match.values.some(value => target.includes(value))
  }

  // 型に無い判定の方法を持つ定義はどの店舗とも一致させない
  // 属さない店舗は AreaLabelManager がまとめて警告するので、店舗ごとには警告しない
  return false
}

/**
 * 判定に使える予約フィールドのキーか
 *
 * フィールド設定に無くても値を引けるので、設定の検証にも使う
 *
 * @param field フィールドのキー
 */
export function isTargetableReservedField(field: string): field is TargetableReservedField {
  return TARGETABLE_RESERVED_FIELDS.some(targetable => targetable === field)
}

/**
 * 判定に使う文字列を店舗から取り出す
 *
 * 追加フィールドは Point.extra_fields に入り、予約フィールドは Point の直下に入る
 * どちらに入るかはフィールド設定の is_extra で決まる
 *
 * フィールド設定が空の組織でも既定の住所判定を動かせるよう、
 * フィールド設定に無いキーは予約フィールドとして扱う
 *
 * @param point 取り出す対象の店舗
 * @param field 判定に使うフィールドのキー
 * @returns 判定に使う文字列、値を持たないか判定に使えないフィールドなら空文字
 */
function _resolveTargetValue(point: Point, field: string): string {
  if (storelocator.fields?.[field]?.is_extra === true) {
    // 追加フィールドは値が未設定なら null が入る
    return point.extra_fields?.[field] ?? ''
  }

  // 判定に使えないフィールドの指定は設定の誤りなので、数値を文字列に直してまで判定させない
  // 全店舗がどのエリアにも一致しなくなり、AreaLabelManager の警告で気づける
  if (!isTargetableReservedField(field)) {
    return ''
  }

  return point[field] ?? ''
}
